import { SeoPageConfig } from "./seo-pages";

/**
 * Посадочные под запросы «расшифровать запись созвона в <платформе>».
 * Тексты намеренно описывают только то, что не устаревает: куда платформа кладёт
 * запись и в каком контейнере, — без названий пунктов меню и версий интерфейса.
 */
export const PLATFORM_PAGES: SeoPageConfig[] = [
  {
    path: "/zoom-transcription",
    cardTitle: "Записи Zoom",
    metaTitle: "Расшифровка записи Zoom в текст | Транскрибация созвонов онлайн",
    metaDescription:
      "Превратите запись встречи в Zoom в текст с разделением по спикерам и таймкодами. Загрузите MP4 или M4A как есть — конвертировать не нужно.",
    customTitle: "Расшифровка записи Zoom в текст",
    customSubtitle: "Загрузите файл записи созвона — получите текст с репликами по спикерам и таймкодами.",
    seoDescription: (
      <>
        <p>
          Zoom сохраняет локальную запись двумя файлами: видео в контейнере MP4 и отдельную аудиодорожку в M4A. Для
          расшифровки годится любой из них — сервис сам достанет звук из видео, а видеоряд проигнорирует. Если запись
          велась в облако, её сначала нужно скачать: работать по ссылке на закрытую облачную запись сервис не может, она
          требует авторизации в вашем аккаунте.
        </p>
        <p>
          Созвоны — самый выигрышный материал для автоматической расшифровки, потому что говорящих обычно несколько и
          вручную разбирать такую запись особенно мучительно. Разделение по спикерам расставляет реплики по ролям, и
          протокол собирается из готового текста за несколько минут вместо часа переслушивания.
        </p>
        <p>
          Качество зависит от того, как участники были записаны. Гарнитура даёт заметно более чистый звук, чем
          встроенный микрофон ноутбука, а несколько человек в одной переговорке за общим микрофоном — самый сложный
          случай для разделения по голосам.
        </p>
        <p>
          У Zoom есть настройка, которая заметно улучшает результат: запись отдельной аудиодорожки на каждого участника.
          Тогда вместо одного смешанного файла получается набор дорожек, где голоса не накладываются друг на друга. Если
          у вас включена эта опция, обрабатывайте дорожки по отдельности — точность будет выше, чем на любом сведённом
          варианте.
        </p>
        <p>
          Локальная запись начинается только после того, как её включат вручную, и сохраняется на диск того, кто её
          запускал. Организатор при этом может запретить запись остальным. Практическое следствие: если встреча важная,
          договаривайтесь о записи в самом начале — восстановить разговор задним числом нельзя.
        </p>
        <p>
          Длинные встречи Zoom иногда режет на несколько файлов подряд. Загружайте их по очереди и склеивайте текст
          после — так надёжнее, чем пытаться объединить видео перед загрузкой. Порядок частей виден по времени создания
          файлов.
        </p>
        <p>
          Отдельно стоит помнить про уведомление участников. Zoom показывает индикатор записи, но согласие на неё —
          вопрос не технический: если разговор потом превращается в текст и расходится по коллегам, участники должны об
          этом знать заранее.
        </p>
      </>
    ),
    faq: [
      {
        question: "Какой файл загружать — видео или аудио?",
        answer:
          "Любой. Из MP4 сервис извлечёт аудиодорожку автоматически, а M4A весит меньше и загрузится быстрее. На точность выбор не влияет."
      },
      {
        question: "Можно ли дать ссылку на облачную запись?",
        answer:
          "Нет, облачные записи закрыты авторизацией и сервису недоступны. Скачайте файл и загрузите его. Ссылки поддерживаются для открытых публикаций на YouTube, VK Video и Rutube."
      },
      {
        question: "Разделит ли сервис реплики участников?",
        answer:
          "Да, реплики группируются по говорящим. Точность выше, когда каждый участник подключён со своего устройства, и ниже, когда несколько человек сидят за одним микрофоном."
      },
      {
        question: "Что делать, если запись длиннее четырёх часов?",
        answer:
          "Разбейте её на части любым аудиоредактором и загрузите по очереди. Ограничение — до 2 ГБ и до четырёх часов на один файл."
      }
    ]
  },
  {
    path: "/google-meet-transcription",
    cardTitle: "Записи Google Meet",
    metaTitle: "Расшифровка записи Google Meet в текст | Транскрибация встреч",
    metaDescription:
      "Расшифровка записей встреч Google Meet в текст с таймкодами и разделением по спикерам. Скачайте MP4 с Google Диска и загрузите файл.",
    customTitle: "Расшифровка записи Google Meet",
    customSubtitle: "Скачайте запись с Google Диска и получите готовый текст встречи с разметкой по участникам.",
    seoDescription: (
      <>
        <p>
          Google Meet складывает записи встреч на Google Диск организатора в виде MP4. Прямую ссылку на файл диска
          сервис использовать не может — доступ к нему закрыт вашей учётной записью, поэтому запись нужно сначала
          скачать, а затем загрузить сюда. Конвертировать ничего не требуется, MP4 принимается как есть.
        </p>
        <p>
          Возможность записи зависит от тарифа рабочего пространства: в бесплатной версии её обычно нет. Если запись не
          велась, но встреча была важной, остаётся вариант с внешним захватом экрана — получившийся файл тоже подойдёт,
          лишь бы в нём был звук.
        </p>
        <p>
          Для встреч с распределённой командой особенно полезно автоопределение языка: если часть разговора шла на
          английском, сервис справится и с этим, а смешанные реплики останутся на языке оригинала.
        </p>
        <p>
          Записи Meet попадают на Диск организатора в отдельную папку и появляются там не мгновенно: после длинной
          встречи файл может обрабатываться какое-то время. Если сразу после созвона записи не видно, это нормально —
          стоит подождать и обновить папку.
        </p>
        <p>
          Meet умеет показывать субтитры в реальном времени, и их иногда путают с расшифровкой. Разница принципиальная:
          живые субтитры нигде не сохраняются и исчезают вместе с окончанием встречи. Чтобы получить текст, с которым
          можно работать дальше, нужна именно запись.
        </p>
        <p>
          Участников Meet предупреждает о записи автоматически, но техническое уведомление не заменяет договорённости.
          Если расшифровка пойдёт дальше круга участников — в протокол, в задачи, в переписку, — об этом лучше сказать
          вслух в начале встречи.
        </p>
        <p>
          Когда запись недоступна из-за тарифа, остаётся запасной вариант: положить рядом телефон с диктофоном. Качество
          будет хуже, чем у системной записи, потому что микрофон снимает звук из динамика, но для расшифровки основных
          договорённостей этого обычно достаточно.
        </p>
      </>
    ),
    faq: [
      {
        question: "Где найти запись встречи?",
        answer:
          "Записи Google Meet сохраняются на Google Диск организатора встречи. Скачайте файл MP4 оттуда и загрузите его на страницу."
      },
      {
        question: "Можно ли расшифровать встречу без записи?",
        answer:
          "Нет, нужен файл. Расшифровка делается по звуку, поэтому запись должна вестись во время встречи — восстановить её задним числом невозможно."
      },
      {
        question: "Поддерживаются ли встречи на нескольких языках?",
        answer:
          "Да. Выберите автоопределение — сервис распознает язык сам. Текст останется на языке оригинала, перевод не выполняется."
      },
      {
        question: "Нужно ли вырезать видео, чтобы уменьшить файл?",
        answer:
          "Не обязательно, лимит 2 ГБ покрывает большинство встреч. Если файл больше, извлеките из него аудиодорожку — размер сократится в разы без потери точности."
      }
    ]
  },
  {
    path: "/teams-transcription",
    cardTitle: "Записи Microsoft Teams",
    metaTitle: "Расшифровка записи Microsoft Teams в текст | Транскрибация встреч",
    metaDescription:
      "Транскрибация записей встреч Microsoft Teams в текст. Скачайте MP4 из OneDrive или SharePoint и получите текст с таймкодами и спикерами.",
    customTitle: "Расшифровка записи Microsoft Teams",
    customSubtitle: "Запись встречи из OneDrive или SharePoint превращается в структурированный текст.",
    seoDescription: (
      <>
        <p>
          Teams сохраняет записи встреч в облачное хранилище организации — OneDrive или SharePoint, в зависимости от
          типа встречи. Файл приходит в формате MP4. Как и в случае с другими корпоративными платформами, ссылка на
          такую запись защищена доменной авторизацией, поэтому файл нужно скачать и загрузить сюда.
        </p>
        <p>
          Собственная расшифровка в Teams существует, но у неё есть заметные ограничения: она доступна не на всех
          тарифах, качество на русском заметно уступает специализированным моделям, а выгрузить результат в удобном виде
          получается не всегда. Отдельная расшифровка даёт текст с пунктуацией, разметкой по спикерам и выгрузкой в
          DOCX, PDF или SRT.
        </p>
        <p>
          Для рабочих созвонов пригодится AI-сводка: она собирает из длинной расшифровки краткое изложение и список
          ключевых моментов, из которых удобно делать протокол.
        </p>
        <p>
          Где именно окажется файл, зависит от типа встречи. Записи созвонов в канале обычно ложатся в хранилище самого
          канала, а личных встреч — в облако того, кто включил запись. Если файла нет там, где вы его ищете, проверьте
          второй вариант: это самая частая причина «запись пропала».
        </p>
        <p>
          Второе, о чём стоит знать заранее, — сроки хранения. В организациях часто настроено автоматическое удаление
          записей через определённое время. Если расшифровка нужна не сразу, файл лучше скачать заранее, а не
          рассчитывать, что он будет лежать в облаке месяцами.
        </p>
        <p>
          Ещё одно ограничение — административное: политики компании могут запрещать скачивание записей наружу. Это не
          техническая проблема, но она решается только внутри организации, и лучше выяснить её до того, как вы
          пообещаете коллегам расшифровку.
        </p>
        <p>
          По качеству звука встречи в Teams обычно ровные: большинство участников подключается с гарнитурой или
          ноутбука, а платформа подавляет фоновый шум. Сложности начинаются там же, где и везде, — когда несколько
          человек сидят в переговорке за одним микрофоном.
        </p>
      </>
    ),
    faq: [
      {
        question: "Чем это лучше встроенной расшифровки Teams?",
        answer:
          "Точностью на русском языке, автоматической пунктуацией и форматами выгрузки. Встроенная расшифровка к тому же доступна не на всех тарифах."
      },
      {
        question: "Где хранится запись встречи?",
        answer:
          "В OneDrive или SharePoint вашей организации — конкретное место зависит от типа встречи. Файл сохраняется в формате MP4."
      },
      {
        question: "Можно ли получить из записи готовый протокол?",
        answer:
          "Да. После расшифровки доступна AI-сводка: краткое изложение встречи и список ключевых моментов, которые остаётся только отредактировать."
      },
      {
        question: "Что если во встрече участвовали десять человек?",
        answer:
          "Разделение по спикерам работает и на больших встречах. Точность выше, когда участники подключены с отдельных устройств, а не сидят в одной переговорке."
      }
    ]
  },
  {
    path: "/telemost-transcription",
    cardTitle: "Записи Яндекс Телемоста",
    metaTitle: "Расшифровка записи Яндекс Телемоста в текст | Транскрибация встреч",
    metaDescription:
      "Расшифровка записей встреч в Яндекс Телемосте в текст с разделением по спикерам. Скачайте файл с Яндекс Диска и загрузите на страницу.",
    customTitle: "Расшифровка записи Яндекс Телемоста",
    customSubtitle: "Запись встречи с Яндекс Диска превращается в текст с репликами по участникам.",
    seoDescription: (
      <>
        <p>
          Записи встреч в Телемосте сохраняются на Яндекс Диск организатора. Скачайте файл и загрузите его сюда — формат
          контейнера значения не имеет, сервис сам определит его и достанет звук. Возможность записи зависит от того,
          какой тариф подключён у организации.
        </p>
        <p>
          Телемост чаще других используют для встреч на русском языке, а это как раз тот случай, где разница между
          моделями распознавания особенно заметна. Автоматическая пунктуация и заглавные буквы дают текст, который
          читается сразу, без правки каждой второй фразы.
        </p>
        <p>
          Если встреча была длинной, ориентируйтесь на таймкоды: каждая реплика привязана ко времени в записи, поэтому
          спорный фрагмент легко переслушать, не перематывая файл наугад.
        </p>
        <p>
          Включить запись может только организатор встречи — участникам эта возможность недоступна. Если созвон вёл
          кто-то другой, файл придётся просить у него: обойти это со своей стороны нельзя.
        </p>
        <p>
          Запись занимает место на Диске, и у длинных встреч это ощутимо. Когда квота заканчивается, новые записи могут
          просто не сохраниться — перед серией долгих созвонов имеет смысл проверить, сколько места осталось.
        </p>
        <p>
          Телемост работает прямо в браузере, без установки программы. Для расшифровки это скорее плюс: участники
          подключаются с ноутбуков, звук идёт с обычных гарнитур и встроенных микрофонов, а не через переотражения в
          комнате. Хуже всего распознаются те, кто зашёл с телефона на громкой связи.
        </p>
        <p>
          Отдельный удобный сценарий — регулярные внутренние встречи. Если созвон повторяется еженедельно, расшифровки
          складываются в хронологию, по которой потом легко восстановить, когда и что решили. Искать по тексту
          несравнимо быстрее, чем пересматривать десяток записей.
        </p>
      </>
    ),
    faq: [
      {
        question: "Где лежит запись встречи?",
        answer: "На Яндекс Диске того, кто включал запись. Скачайте файл оттуда и загрузите его на страницу."
      },
      {
        question: "Нужно ли конвертировать файл перед загрузкой?",
        answer:
          "Нет. Формат определяется автоматически, аудиодорожка извлекается из видео тоже автоматически — загружайте файл как есть."
      },
      {
        question: "Сохраняются ли таймкоды?",
        answer:
          "Да, каждая реплика привязана ко времени в записи. Это удобно, когда нужно вернуться к конкретному моменту обсуждения."
      },
      {
        question: "Что делать, если запись не велась?",
        answer:
          "Расшифровать встречу задним числом невозможно — нужен звуковой файл. На будущее имеет смысл договариваться о записи в начале встречи."
      }
    ]
  },
  {
    path: "/vk-calls-transcription",
    cardTitle: "Записи VK Звонков",
    metaTitle: "Расшифровка записи VK Звонков в текст | Транскрибация созвонов",
    metaDescription:
      "Транскрибация записей VK Звонков в текст с таймкодами и разделением по спикерам. Скачайте запись и загрузите файл — конвертация не нужна.",
    customTitle: "Расшифровка записи VK Звонков",
    customSubtitle: "Скачанная запись созвона превращается в текст с разметкой по говорящим.",
    seoDescription: (
      <>
        <p>
          Записи звонков во «ВКонтакте» доступны организатору после завершения разговора. Скачайте файл и загрузите его
          сюда — сервис сам разберётся с контейнером и достанет аудиодорожку. Отдельно конвертировать запись не нужно.
        </p>
        <p>
          Такие созвоны часто ведутся в неидеальных условиях: кто-то подключается с телефона, кто-то из шумного места.
          Модель устойчива к фоновому шуму, но чем чище звук у каждого участника, тем точнее и распознавание, и
          разделение по спикерам.
        </p>
        <p>
          Готовый текст выгружается в TXT, DOCX, PDF или SRT, а по длинной записи можно сразу получить AI-сводку с
          ключевыми моментами — это заметно быстрее, чем вычитывать всю расшифровку целиком.
        </p>
        <p>
          Главная особенность этой платформы для расшифровки — аудитория сидит преимущественно с телефонов. Микрофон
          смартфона у лица даёт неплохой звук, но стоит человеку положить телефон на стол или включить громкую связь,
          как разборчивость падает, а вместе с ней и точность разделения по спикерам.
        </p>
        <p>
          Отсюда простая рекомендация участникам: гарнитура или наушники с микрофоном меняют результат сильнее, чем
          любые настройки обработки. Разница между разговором в наушниках и разговором «в комнату» видна в тексте сразу
          — по количеству потерянных окончаний и слипшихся реплик.
        </p>
        <p>
          Записи звонков доступны после завершения разговора и хранятся не бесконечно. Если расшифровка нужна не
          сегодня, файл стоит скачать сразу — это надёжнее, чем возвращаться за ним через месяц.
        </p>
        <p>
          Короткие звонки на две-три минуты обрабатываются так же, как длинные, и это удобно для быстрой фиксации
          договорённостей. Но если таких звонков много, выгоднее склеить их в один файл: минимальная тарификация делает
          обработку десятка коротких записей по отдельности менее выгодной.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли расшифровать звонок по ссылке?",
        answer:
          "Ссылки поддерживаются для публичных видео на VK Video, YouTube и Rutube. Запись личного звонка нужно скачать и загрузить файлом."
      },
      {
        question: "Влияет ли фоновый шум на результат?",
        answer:
          "Да, но модель к нему устойчива. Сильнее всего мешают одновременная речь нескольких человек и запись с большого расстояния до микрофона."
      },
      {
        question: "В каком виде приходит результат?",
        answer: "Текст с пунктуацией, таймкодами и разделением по спикерам. Выгрузить можно в TXT, DOCX, PDF или SRT."
      },
      {
        question: "Есть ли ограничение по длине звонка?",
        answer: "До 2 ГБ и до четырёх часов на файл. Более длинные записи нужно разбить на части."
      }
    ]
  },
  {
    path: "/kontur-talk-transcription",
    cardTitle: "Записи Контур.Толка",
    metaTitle: "Расшифровка записи Контур.Толка в текст | Транскрибация встреч",
    metaDescription:
      "Расшифровка записей встреч в Контур.Толке в текст с разделением по спикерам и таймкодами. Загрузите скачанный файл — конвертация не нужна.",
    customTitle: "Расшифровка записи Контур.Толка",
    customSubtitle: "Запись рабочей встречи превращается в текст, из которого собирается протокол.",
    seoDescription: (
      <>
        <p>
          Контур.Толк используют в основном для рабочих встреч, а у них своя специфика: много участников, деловая
          терминология и необходимость зафиксировать договорённости. Скачайте запись встречи и загрузите файл — сервис
          определит формат сам и достанет звук из видеоконтейнера.
        </p>
        <p>
          Для протокола удобнее всего связка из двух вещей: разделения по спикерам, которое показывает, кто именно что
          предложил, и AI-сводки, которая сжимает часовое обсуждение до списка ключевых моментов. Дальше остаётся
          проверить формулировки и разослать.
        </p>
        <p>
          Профессиональная лексика распознаётся нормально: модель опирается на контекст всего предложения, а не на
          отдельные слова, поэтому термины редко превращаются в бессмыслицу.
        </p>
        <p>
          Корпоративные встречи почти всегда содержат то, что не предназначено для посторонних: цифры, условия сделок,
          персональные оценки сотрудников. Перед тем как выносить запись за пределы компании, стоит свериться с
          внутренними правилами — это вопрос не технологии, а регламента, и решается он на вашей стороне.
        </p>
        <p>
          Совещания в Толке отличаются от переговоров структурой: обычно есть ведущий, повестка и несколько выступающих
          по очереди. Для распознавания это благодарный формат — реплики не накладываются, и разделение по спикерам
          работает точнее, чем на свободной дискуссии.
        </p>
        <p>
          Слабое место у таких встреч одно и то же — переговорная комната. Когда пять человек сидят за столом вокруг
          одного ноутбука, звук приходит с разного расстояния и с эхом от стен. Тех, кто дальше от микрофона, модель
          распознаёт заметно хуже, и в тексте это видно.
        </p>
        <p>
          Если встречи регулярные, расшифровки удобно называть по дате и теме сразу при сохранении. Через полгода именно
          это определяет, найдёте вы нужное решение за минуту или будете открывать файлы по одному.
        </p>
      </>
    ),
    faq: [
      {
        question: "Справится ли сервис с профессиональной терминологией?",
        answer:
          "В большинстве случаев да — распознавание опирается на контекст предложения. Редкие узкоспециальные термины и аббревиатуры стоит проверить при вычитке."
      },
      {
        question: "Как быстро получить протокол встречи?",
        answer:
          "После расшифровки запустите AI-сводку: она соберёт краткое изложение и ключевые моменты, которые останется только отредактировать."
      },
      {
        question: "Нужно ли отдельно извлекать звук из видео?",
        answer: "Нет, это делается автоматически. Загружайте файл записи как есть."
      },
      {
        question: "Видно ли, кто из участников говорил?",
        answer:
          "Реплики группируются по говорящим, но имена сервис не знает — участники обозначаются как «Спикер 1», «Спикер 2» и так далее. Подписать их можно вручную."
      }
    ]
  },
  {
    path: "/webinar-transcription",
    cardTitle: "Записи вебинаров",
    metaTitle: "Расшифровка вебинара в текст онлайн | Транскрибация записи вебинара",
    metaDescription:
      "Транскрибация записи вебинара в текст с таймкодами. Готовый конспект, статья или субтитры из часовой записи за несколько минут.",
    customTitle: "Расшифровка вебинара в текст",
    customSubtitle: "Часовая запись превращается в конспект, статью или субтитры.",
    seoDescription: (
      <>
        <p>
          Вебинар — это почти всегда один говорящий и час-полтора связной речи, то есть идеальный материал для
          распознавания. Загрузите скачанную запись файлом или вставьте ссылку, если вебинар опубликован на YouTube, VK
          Video или Rutube.
        </p>
        <p>
          Расшифровка вебинара обычно нужна не сама по себе, а как заготовка: из неё делают конспект для участников,
          статью в блог или текстовую версию для тех, кто не любит смотреть видео. Таймкоды позволяют разбить материал
          на смысловые части, а выгрузка в SRT — сразу получить субтитры к записи.
        </p>
        <p>
          Если в конце был блок вопросов, разделение по спикерам отделит реплики зала от ответов ведущего, и эту часть
          будет проще вычитывать отдельно.
        </p>
        <p>
          Вебинарные платформы отдают записи в MP4 или WEBM — оба формата принимаются как есть. Час-полтора записи
          укладываются в лимиты с большим запасом, так что готовить файл перед загрузкой обычно не приходится вовсе.
        </p>
        <p>
          Из одной расшифровки получается сразу несколько материалов, и это главная причина её делать. Статья в блог,
          письмо участникам с ключевыми тезисами, текстовая версия для тех, кто не смотрит видео, и субтитры к самой
          записи — всё это собирается из одного текста за вечер вместо недели.
        </p>
        <p>
          Устную речь при этом придётся редактировать. Ведущий повторяется, возвращается к сказанному, использует
          вводные слова — в живом рассказе это незаметно, а на бумаге бросается в глаза. Расшифровка даёт структуру и
          содержание, но не готовый текст для публикации.
        </p>
        <p>
          Вопросы из зала стоит рассматривать отдельно. Часть из них приходит текстом в чат и в запись не попадает, а
          заданные голосом звучат тише основного доклада. Разделение по спикерам отделит их от ответов ведущего, но
          именно эти фрагменты чаще всего требуют проверки по таймкодам.
        </p>
      </>
    ),
    faq: [
      {
        question: "Можно ли расшифровать вебинар по ссылке?",
        answer:
          "Да, если он опубликован на YouTube, VK Video или Rutube — достаточно вставить ссылку. Записи из закрытых платформ нужно скачать и загрузить файлом."
      },
      {
        question: "Как получить из вебинара статью?",
        answer:
          "Возьмите расшифровку и запустите AI-сводку: она выделит структуру и ключевые мысли. Останется переписать разговорные обороты в письменные."
      },
      {
        question: "Можно ли сделать субтитры к записи?",
        answer: "Да, результат выгружается в SRT — этот файл подключается к видео как дорожка субтитров."
      },
      {
        question: "Что делать с вебинаром длиннее четырёх часов?",
        answer: "Разбейте запись на части и загрузите по очереди. Ограничение — до 2 ГБ и четырёх часов на один файл."
      }
    ]
  }
];
